import { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, Popper, styled, useTheme } from "@mui/material";
import { getCoreRowModel, Header, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as Papa from "papaparse";
import TextOverflow from "~/components/TextOverflow";

const ROW_HEIGHT = 40;

const StyledTable = styled("table")(({ theme }) => ({
  display: "grid",
  "thead, tr:not(:last-of-type)": {
    overflow: "clip",
    "td, th": {
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  "td, th": {
    textWrap: "nowrap",
    height: ROW_HEIGHT,
    padding: theme.spacing(1),
    "&:not(:first-of-type)": { borderLeft: `1px solid ${theme.palette.grey[300]}` },
    "&.resizing": {
      borderRight: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  th: {
    background: theme.palette.grey[50],
  },
}));

const ColumnResizer = (props: { header: Header<unknown, unknown> }) => (
  <Box
    {...{
      onDoubleClick: () => props.header.column.resetSize(),
      onMouseDown: props.header.getResizeHandler(),
      onTouchStart: props.header.getResizeHandler(),
    }}
    sx={{
      position: "absolute",
      display: "flex",
      justifyContent: "center",
      top: 0,
      right: -6,
      width: 10,
      zIndex: 5,
      height: "100%",
      cursor: "col-resize",
      userSelect: "none",
      touchAction: "none",
      transition: `opacity 300ms ease-in-out`,
      opacity: props.header.column.getIsResizing() ? 0.6 : 0,
      "&:hover": { opacity: 0.6 },
    }}
  >
    <Box
      sx={{
        my: 0.75,
        width: "100%",
        borderLeft: 2,
        borderRight: 2,
        borderColor: props.header.column.getIsResizing() ? "grey.500" : "grey.400",
      }}
    />
  </Box>
);

const TablePreview = (props: { fileData: string }) => {
  const { palette } = useTheme();
  const [selectedCell, setSelectedCell] = useState<null | HTMLTableCellElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const containerWidth = tableContainerRef.current?.clientWidth ?? window.innerWidth;
  const data = useMemo(() => {
    const parseResult = Papa.parse(props.fileData, { header: true, skipEmptyLines: true });
    return parseResult.data;
  }, [props.fileData]);

  const columns = useMemo(() => {
    if (data[0]) {
      return Object.keys(data[0])
        .filter(Boolean)
        .map((key) => ({
          header: key,
          accessorKey: key,
        }));
    }
    return [];
  }, [data]);

  const table = useReactTable({
    data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    defaultColumn: {
      size: Math.max(125, containerWidth / columns.length),
      minSize: Math.max(50, containerWidth / columns.length),
      maxSize: Math.max(500, containerWidth / columns.length),
    },
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => ROW_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    overscan: 20,
  });

  useEffect(() => {
    if (popperRef.current && selectedCell) {
      let snappedWidth = 0;
      const columns = table.getAllColumns();
      const initialWidth = popperRef.current.offsetWidth;
      for (let i = selectedCell?.cellIndex; i <= columns.length - 1; i++) {
        if (snappedWidth >= initialWidth) {
          break;
        }
        snappedWidth += columns[i].getSize();
      }
      // Snap expanded cell popover to next column if overflowing
      popperRef.current.style.width = `${snappedWidth}px`;

      const initialHeight = popperRef.current.offsetHeight;
      if (initialHeight > ROW_HEIGHT + 1) {
        const snappedHeight = Math.ceil(initialHeight / ROW_HEIGHT) * ROW_HEIGHT;
        const tableBottom = tableContainerRef.current?.getBoundingClientRect().bottom ?? 0;
        const heightRemaining = tableBottom - selectedCell.getBoundingClientRect().top;
        // Snap expanded cell height to next row if overflowing (row height + border bottom)
        popperRef.current.style.height = `${Math.min(snappedHeight, heightRemaining)}px`;
      }
    }
  }, [table, selectedCell, tableContainerRef.current, popperRef.current]);

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        overflow: "auto",
        position: "relative",
        bgcolor: "common.white",
        borderRadius: 1,
        mx: 2,
        // Set virtual list height to remaining viewport space or height of content, whichever is less
        height: `min(100%, ${rowVirtualizer.getTotalSize() + ROW_HEIGHT + 1}px)`,
      }}
    >
      <StyledTable>
        <thead
          style={{
            display: "grid",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ display: "flex", width: "100%" }}>
              {headerGroup.headers.map((header) => {
                const isResizing = header.column.getIsResizing();
                return (
                  <Box
                    component="th"
                    className={isResizing ? "resizing" : ""}
                    key={header.id}
                    sx={{
                      display: "flex",
                      position: "relative",
                      width: header.getSize(),
                    }}
                  >
                    <TextOverflow>{header.column.columnDef.header as string}</TextOverflow>
                    <ColumnResizer header={header} />
                  </Box>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          style={{
            display: "grid",
            height: `${rowVirtualizer.getTotalSize()}px`,
            overflow: "clip",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <tr
                data-index={virtualRow.index} // Used for dynamic row height measurement
                ref={(node) => rowVirtualizer.measureElement(node)}
                key={row.id}
                style={{
                  display: "flex",
                  position: "absolute",
                  transform: `translateY(${virtualRow.start}px)`,
                  width: "100%",
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isResizing = cell.column.getIsResizing();
                  return (
                    <td
                      key={cell.id}
                      className={isResizing ? "resizing" : ""}
                      onClick={(e) => setSelectedCell(e.currentTarget)}
                      style={{
                        display: "flex",
                        backgroundColor: palette.common.white,
                        width: cell.column.getSize(),
                      }}
                    >
                      <TextOverflow>{cell.renderValue() as string}</TextOverflow>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <Popper
          open={!!selectedCell}
          anchorEl={selectedCell}
          placement="bottom-start"
          disablePortal
          modifiers={[
            { name: "flip", enabled: false },
            {
              name: "preventOverflow",
              enabled: false,
            },
            { name: "offset", options: { offset: [0, -ROW_HEIGHT] } },
          ]}
        >
          <ClickAwayListener onClickAway={() => setSelectedCell(null)} mouseEvent="onMouseDown">
            <Box
              ref={popperRef}
              sx={{
                borderRight: 1,
                borderColor: "grey.300",
                p: 1,
                pl: "9px",
                overflowY: "auto",
                bgcolor: "common.white",
                minHeight: ROW_HEIGHT + 1,
                boxShadow: `inset 0.5px -0.5px 3px 1.5px ${palette.grey[300]}`,
              }}
            >
              {selectedCell?.innerText}
            </Box>
          </ClickAwayListener>
        </Popper>
      </StyledTable>
    </Box>
  );
};

export default TablePreview;
